import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  Activity,
  Building2,
  ChevronsLeft,
  Clapperboard,
  ChevronsRight,
  FileSignature,
  FolderOpen,
  Handshake,
  LayoutDashboard,
  LogOut,
  Mail,
  MessageSquare,
  Plus,
  Moon,
  Settings,
  ShieldCheck,
  Sun,
  Users,
  X,
  type LucideIcon,
} from "lucide-react";
import clsx from "clsx";
import { useAuthStore } from "@/store/auth";
import { useUIStore } from "@/store/ui";
import { Avatar } from "@/components/ui";
import { useResolvedTheme } from "@/lib/theme";
import { useIsDesktop } from "@/lib/media";
import { Assistant } from "@/api/assistant";
import { useChatStore } from "@/store/chat";

type NavItem = { to: string; label: string; icon: LucideIcon; key?: string; adminOnly?: boolean };

// Keys match the single-key shortcuts registered in Layout.
const NAV: NavItem[] = [
  { to: "/", label: "Home", icon: MessageSquare, key: "H" },
  { to: "/today", label: "Today", icon: Sun, key: "T" },
  { to: "/deals", label: "Deals", icon: Handshake, key: "D" },
  { to: "/contracts", label: "Contracts", icon: FileSignature, key: "K" },
  { to: "/accounts", label: "Accounts", icon: Building2, key: "A" },
  { to: "/contacts", label: "Contacts", icon: Users, key: "C" },
  { to: "/library", label: "Library", icon: FolderOpen, key: "L" },
  { to: "/demos", label: "Demos", icon: Clapperboard, key: "M" },
  { to: "/activity", label: "Activity", icon: Activity, key: "E" },
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, key: "R", adminOnly: true },
  { to: "/admin", label: "Admin", icon: ShieldCheck, adminOnly: true },
  { to: "/settings", label: "Settings", icon: Settings, key: "," },
];

export default function Sidebar() {
  const { user, logout } = useAuthStore();
  const navigate = useNavigate();
  const setLastChatId = useChatStore((s) => s.setLastChatId);
  const { sidebarCollapsed, toggleSidebar, mobileNavOpen, setMobileNavOpen } = useUIStore();
  const desktop = useIsDesktop();
  // On a phone the sidebar is a full-width drawer, never the icon rail.
  const collapsed = desktop && sidebarCollapsed;
  const isAdmin = user?.role === "admin";
  const navItems = NAV.filter((item) => !item.adminOnly || isAdmin);

  return (
    <>
      {!desktop && mobileNavOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/30 backdrop-blur-[2px]"
          onClick={() => setMobileNavOpen(false)}
        />
      )}
      <aside
        className={clsx(
          "flex flex-col border-r border-fg/[0.06]",
          desktop
            ? "sticky top-0 h-screen shrink-0 bg-ink-800/70"
            : "fixed inset-y-0 left-0 z-50 h-[100dvh] w-[min(300px,85vw)] bg-ink-800 shadow-2xl",
          desktop && (collapsed ? "w-[64px]" : "w-[232px]"),
          !desktop && !mobileNavOpen && "invisible -translate-x-full",
        )}
        style={{
          transition: desktop
            ? "width .3s var(--ease)"
            : "transform .25s var(--ease), visibility .25s",
        }}
        aria-hidden={!desktop && !mobileNavOpen ? true : undefined}
      >
        {/* Brand + collapse toggle */}
        <div
          className={clsx(
            "flex items-center gap-2.5 pt-4 pb-3",
            collapsed ? "justify-center px-2" : "px-4",
          )}
        >
          <img
            src="/favicon.svg"
            alt={collapsed ? "LeadZeppelin" : ""}
            width={28}
            height={28}
            className="rounded-lg shrink-0"
          />
          {!collapsed && (
            <>
              <div className="flex-1 min-w-0 font-semibold text-base tracking-tight">
                LeadZeppelin
              </div>
              {desktop ? (
                <button
                  onClick={toggleSidebar}
                  title="Collapse sidebar"
                  className="btn btn-ghost !p-1.5"
                >
                  <ChevronsLeft className="w-4 h-4" />
                </button>
              ) : (
                <button
                  onClick={() => setMobileNavOpen(false)}
                  aria-label="Close menu"
                  className="btn btn-ghost !p-2"
                >
                  <X className="w-5 h-5" />
                </button>
              )}
            </>
          )}
        </div>
        {collapsed && (
          <button
            onClick={toggleSidebar}
            title="Expand sidebar"
            className="btn btn-ghost mx-2 mb-1 !p-1.5"
          >
            <ChevronsRight className="w-4 h-4" />
          </button>
        )}

        <div className={clsx("mt-1", collapsed ? "px-2" : "px-3")}>
          <button
            onClick={() => {
              setLastChatId(null);
              setMobileNavOpen(false);
              navigate("/");
            }}
            title={collapsed ? "New chat (N)" : undefined}
            className={clsx(
              "group flex w-full items-center gap-2.5 rounded-[10px] py-2 text-md font-semibold text-mut-100 transition-colors hover:bg-fg/[0.05]",
              collapsed ? "justify-center px-1" : "px-2.5",
            )}
          >
            <span
              className="grid h-6 w-6 shrink-0 place-items-center rounded-full text-white shadow-sm transition-transform group-hover:scale-105"
              style={{ background: "var(--grad)" }}
            >
              <Plus className="h-3.5 w-3.5" strokeWidth={2.5} />
            </span>
            {!collapsed && (
              <>
                <span className="flex-1 text-left">New chat</span>
                {desktop && <span className="kbd opacity-70">N</span>}
              </>
            )}
          </button>
        </div>

        <nav className={clsx("mt-1 shrink-0", collapsed ? "px-2" : "px-3")}>
          {navItems.map(({ to, label, icon: Icon, key }) => (
            <NavLink
              key={to}
              to={to}
              end={to === "/"}
              onClick={() => setMobileNavOpen(false)}
              title={collapsed ? label : undefined}
              className={({ isActive }) =>
                clsx("nav-item", collapsed && "justify-center !px-1", isActive && "active")
              }
            >
              <Icon className="w-4 h-4 shrink-0" />
              {!collapsed && (
                <>
                  <span className="truncate flex-1">{label}</span>
                  {key && desktop && <span className="kbd opacity-70">{key}</span>}
                </>
              )}
            </NavLink>
          ))}
        </nav>

        {collapsed ? <div className="flex-1" /> : <Recents />}

        {/* User */}
        {collapsed && (
          <div className="flex justify-center">
            <ThemeToggle />
          </div>
        )}
        <div
          className={clsx(
            "m-3 flex items-center gap-2.5 rounded-xl",
            collapsed ? "justify-center p-1" : "px-2 py-2",
          )}
        >
          <Avatar name={user?.name} size={30} />
          {!collapsed && (
            <>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium truncate">{user?.name ?? "—"}</div>
                <div className="text-2xs text-mut-500 truncate">{user?.email}</div>
              </div>
              <ThemeToggle />
              <button
                onClick={logout}
                title="Sign out"
                aria-label="Sign out"
                className="btn btn-ghost !p-1.5"
              >
                <LogOut className="w-3.5 h-3.5" />
              </button>
            </>
          )}
        </div>
      </aside>
    </>
  );
}

/** One click between light and dark. (Settings also offers "System".) */
function ThemeToggle() {
  const setTheme = useUIStore((s) => s.setTheme);
  const dark = useResolvedTheme() === "dark";
  const label = dark ? "Switch to light theme" : "Switch to dark theme";
  const Icon = dark ? Sun : Moon;
  return (
    <button
      onClick={() => setTheme(dark ? "light" : "dark")}
      title={label}
      aria-label={label}
      className="btn btn-ghost !p-1.5"
    >
      <Icon className="w-3.5 h-3.5" />
    </button>
  );
}

/** Past conversations, newest first, like Claude's sidebar. */
function Recents() {
  const navigate = useNavigate();
  const location = useLocation();
  const setLastChatId = useChatStore((s) => s.setLastChatId);
  const setMobileNavOpen = useUIStore((s) => s.setMobileNavOpen);
  const convsQuery = useQuery({
    queryKey: ["assistant-conversations"],
    queryFn: Assistant.conversations,
    staleTime: 30_000,
  });
  const openId = location.pathname === "/" ? new URLSearchParams(location.search).get("c") : null;
  const chats = convsQuery.data ?? [];

  return (
    <div className="mt-4 flex min-h-0 flex-1 flex-col px-3">
      <div className="flex items-center justify-between px-3 pb-1">
        <span className="section-title">Recents</span>
        <button
          className="btn btn-ghost !p-1"
          title="New chat"
          aria-label="New chat"
          onClick={() => {
            setLastChatId(null);
            setMobileNavOpen(false);
            navigate("/");
          }}
        >
          <Plus className="h-3.5 w-3.5" />
        </button>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto pb-2">
        {chats.length === 0 ? (
          <p className="px-3 py-1.5 text-xs text-mut-500">Your chats will appear here.</p>
        ) : (
          chats.map((c) => (
            <button
              key={c.id}
              title={c.title}
              onClick={() => {
                setMobileNavOpen(false);
                navigate(`/?c=${c.id}`);
              }}
              className={clsx(
                "flex w-full items-center gap-2 rounded-lg px-3 py-1.5 text-left text-sm transition-colors",
                c.id === openId
                  ? "bg-ink-900 text-mut-100 shadow-ring"
                  : "text-mut-300 hover:bg-fg/[0.05] hover:text-mut-100",
              )}
            >
              {c.kind === "inbox" && <Mail className="h-3 w-3 shrink-0 text-accent-violet" />}
              <span className="truncate">{c.title}</span>
              {c.status === "running" && (
                <span className="ml-auto h-1.5 w-1.5 shrink-0 animate-pulse rounded-full bg-accent-azure" />
              )}
            </button>
          ))
        )}
      </div>
    </div>
  );
}
